import React, { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from "material-react-table";
import { useNavigate } from "react-router-dom";
import FunctionsIcon from "@mui/icons-material/Functions";
import ConstructionIcon from "@mui/icons-material/Construction";
import {
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Autocomplete,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { secondAxios } from "../../../../api/axios";
import paths from "../../../../api/paths.api";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import DescriptionIcon from "@mui/icons-material/Description";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import PlantModuleNav from "../../../../components/PlantModuleNav";
import { useAuthContext } from "../../../../context/authContext";
import { useAIMSContext } from "../../../../context/AIMSContext";
import useModuleAccess from "../../../../hooks/useModuleAccess";
import { filterColumnsByLicence } from "../../../../constants/appModules";

const buildPlantName = (prefix, number) =>
  [prefix, number].filter(Boolean).join("-");

export default function PlantsTableComponent({ plants }) {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [state, setState] = useState({
    isLoading: false,
    isError: false,
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // 'create' or 'edit'
  const [currentPlant, setCurrentPlant] = useState({
    name: "",
    namePrefix: "",
    nameNumber: "",
    clientId: "",
    description: "",
    sharepointLink: "",
    people: [],
  });
  const { user } = useAuthContext();
  const { hasModule, allowedModules } = useModuleAccess();
  console.log("user", user.client);

  const { AIMSPlantId, setAIMSPlantId } = useAIMSContext();

  // Load users for people selection
  const fetchUsers = async () => {
    try {
      const res = await secondAxios.get("/user");
      setAllUsers(res.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await secondAxios.get("/client");
      setAllClients(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchClients();
  }, [user]);

  // Open modal for creating new plant
  const handleOpenCreateModal = () => {
    setModalMode("create");
    setCurrentPlant({
      name: "",
      namePrefix: "",
      nameNumber: "",
      clientId: "",
      description: "",
      sharepointLink: "",
      people: [],
    });
    setModalOpen(true);
  };

  // Open modal for editing existing plant
  const handleOpenEditModal = (plant) => {
    setModalMode("edit");
    // Extract user IDs from people array if it exists
    const peopleIds = plant.people
      ? plant.people.map((user) => (typeof user === "string" ? user : user.id))
      : [];
    setCurrentPlant({
      ...plant,
      namePrefix: "",
      nameNumber: "",
      clientId: plant.clientId || plant.client?.id || "",
      people: peopleIds,
    });
    setModalOpen(true);
  };

  // Close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentPlant({
      name: "",
      namePrefix: "",
      nameNumber: "",
      clientId: "",
      description: "",
      sharepointLink: "",
      people: [],
    });
  };

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setCurrentPlant((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClientChange = (client) => {
    const prefix = client?.prefix
      ? String(client.prefix).replace(/[^a-zA-Z0-9]/g, "").toUpperCase()
      : "";
    setCurrentPlant((prev) => {
      const next = {
        ...prev,
        clientId: client?.id || "",
        namePrefix: prefix,
      };
      return {
        ...next,
        name:
          modalMode === "create"
            ? buildPlantName(next.namePrefix, next.nameNumber)
            : prev.name,
      };
    });
  };

  const handleNameNumberChange = (value) => {
    // Allow strings like p001 or p-001
    const sanitized = value.replace(/[^a-zA-Z0-9-]/g, "");
    setCurrentPlant((prev) => {
      const next = { ...prev, nameNumber: sanitized };
      return {
        ...next,
        name: buildPlantName(next.namePrefix, next.nameNumber),
      };
    });
  };

  // Handle people selection change
  const handlePeopleChange = (selectedUsers) => {
    const userIds = selectedUsers.map((user) =>
      typeof user === "string" ? user : user.id,
    );
    setCurrentPlant((prev) => ({
      ...prev,
      people: userIds,
    }));
  };

  // Columns definition
  const columns = useMemo(
    () =>
      filterColumnsByLicence(
        [
      // { accessorKey: "id", header: "ID", size: 50 },
      {
        accessorKey: "name",
        header: "Name",
        // enableColumnFilter: true
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 200,
        enableColumnFilter: true,
      },
      // {
      //   accessorKey: "quickNav",
      //   header: "Quick Nav",
      //   size: 50,
      //   enableColumnFilter: false,
      //   enableColumnActions: false,
      //   Cell: ({ cell }) => (
      //     <PlantModuleNav
      //       plantId={cell.row.original.id}
      //       compact
      //       sx={{ display: "inline-flex" }}
      //     />
      //   ),
      // },
      {
        id: "client",
        accessorFn: (row) => row.client?.name || row.client?.prefix || "-",
        header: "Client",
        size: 40,
        filterVariant: "select",
      },
      {
        accessorKey: "tasks",
        header: "Action Items",
        size: 40,
        enableEditing: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        Edit: () => null,
        Cell: ({ cell }) => {
          return (
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/plant/${cell.row.original.id}/task`);
              }}
            >
              <AssignmentIcon color="primary" />
            </IconButton>
          );
        },
      },
      // {
      //   accessorKey: "ga",
      //   header: "GA",
      //   Cell: ({ cell }) => {
      //     return (
      //       // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
      //       <Box
      //         display="flex"
      //         alignItems="center"
      //         gap={1}
      //         noNavigate={true}
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           // console.log(
      //           //   "mcs clicked",
      //           //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
      //           //   cell.row.original
      //           // );
      //           navigate(
      //             `/projects/${
      //               cell.row.original.id
      //             }/equipments/main_tank#main-nav=${"ga"}&mcs-tab=Spec`
      //           );
      //         }}
      //       >
      //         <CategoryIcon />
      //       </Box>
      //     );
      //   },
      // },
      {
        accessorKey: "estimation",
        header: "Estimation",
        size: 20,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          return (
            // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                // console.log(
                //   "mcs clicked",
                //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
                //   cell.row.original
                // );
                navigate(`/plant/${cell.row.original.id}/estimation`);
              }}
            >
              <FunctionsIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "dcs",
        header: "DCS",
        size: 40,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          return (
            // /projects/2b75e738-159c-4907-b605-1489f5c653c6/equipments/main_tank#main-nav=pv&mcs-tab=Spec&pv-modeling=Estimation
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                // console.log(
                //   "mcs clicked",
                //   // `/projects/${cell.row.original.id}/equipments/main_tank#main-nav=${cell.row.original.type}&mcs-tab=Doc.%Estimation`
                //   cell.row.original
                // );
                navigate(`/plant/${cell.row.original.id}/dcs`);
              }}
            >
              <DocumentScannerIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "mcs",
        header: "MCS",
        size: 40,
        enableEditing: false,
        Edit: () => null,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ cell, row }) => {
          // console.log("row", row.original);
          // const users = row.original?.users || [];

          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                // console.log("mcs clicked");
                navigate(`/plant/${cell.row.original.id}/mcs`);
                // navigate(
                //   `/projects/${row.original.id}/equipments/main_tank#main-nav=MCS&mcs-tab=Drawing%20BOM`
                // );
              }}
              // disabled={true}
            >
              <ManageAccountsIcon color="primary" />
            </IconButton>
          );
        },
      },

      {
        accessorKey: "pcs",
        header: "PCS",
        size: 40,
        enableEditing: false,
        Edit: () => null,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          return (
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/plant/${cell.row.original.id}/pcs`);
              }}
            >
              <ConstructionIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "cms",
        header: "CMS",
        size: 40,
        enableEditing: false,
        Edit: () => null,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          return (
            <IconButton
              display="flex"
              alignItems="center"
              gap={1}
              noNavigate={true}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/plant/${cell.row.original.id}/cms`);
              }}
            >
              <ChangeCircleIcon color="primary" />
            </IconButton>
          );
        },
      },
      {
        accessorKey: "pms",
        header: "PMS",
        size: 40,
        enableEditing: false,
        enableColumnActions: false,
        enableColumnFilter: false,
        Edit: () => null,
        Cell: ({ row, cell }) => {
          return (
            // <a
            //   href={`/projects/${row.original.id}/equipments/main_tank#main-nav=PMS&pms-tab=4D`}
            // >
            <CalendarTodayIcon
              onClick={(e) => {
                console.log("pms clicked");
                e.stopPropagation();
                navigate(`/plant/${cell.row.original.id}/pms`);
              }}
              color="primary"
            />
            // </a>
          );
        },
      },

      {
        accessorKey: "mis",
        header: "MIS",
        size: 40,
        enableEditing: false,
        Edit: () => null,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: ({ cell, row }) => {
          // console.log("row", row.original);
          // const users = row.original?.users || [];

          return (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                // console.log("mcs clicked");
                navigate(`/plant/${cell.row.original.id}/mis`);
                // navigate(
                //   `/projects/${row.original.id}/equipments/main_tank#main-nav=MCS&mcs-tab=Drawing%20BOM`
                // );
              }}
              // disabled={true}
            >
              <DescriptionIcon color="primary" />
            </IconButton>
          );
        },
      },

      {
        accessorKey: "people",
        header: "People",
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const people = cell.getValue();
          if (!people || !Array.isArray(people) || people.length === 0) {
            return <span>-</span>;
          }
          return (
            <span>
              {people
                .map((user) =>
                  typeof user === "object" ? user.name || user.email : user,
                )
                .join(", ")}
            </span>
          );
        },
      },
      {
        accessorKey: "projects",
        header: "No. sub projects",
        enableColumnFilter: false,
        Cell: ({ cell, row }) => {
          const plant = row.original;
          const projects = plant.projects || plant.project;

          if (!projects) {
            return <span>0</span>;
          }

          // Handle different data structures
          const projectsArray = Array.isArray(projects)
            ? projects
            : projects
              ? [projects]
              : [];

          // Filter by client name if needed
          const clientName = process.env.REACT_APP_CLIENT_NAME;
          const filteredProjects = projectsArray.filter(
            (p) => p && p.clientName === clientName,
          );

          return <span>{filteredProjects.length}</span>;
        },
      },
        ],
        hasModule,
        allowedModules,
        user,
      ),
    [AIMSPlantId, hasModule, allowedModules, user],
  );

  return (
    <>
      <MaterialReactTable
        data={plants}
        columns={columns}
        enableRowActions
        enableFacetedValues
        // initialState={{ showColumnFilters: true }}
        positionActionsColumn="last"
        muiTableHeadCellProps={{
          sx: {
            fontSize: "1rem",
            fontWeight: "bold",
            borderRight: "1px solid rgba(224, 224, 224, 1)",
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: "1rem",
            borderRight: "1px solid rgba(224, 224, 224, 1)",
          },
        }}
        muiTableBodyRowProps={({ row }) => ({
          onClick: (event) => {
            const target = event.target;
            if (
              !target.closest(".MuiButtonBase-root") &&
              !target.closest("a")
            ) {
              // console.log("id", paths.plant, row.original);
              // navigate(paths.plant + "/" + row.original.id);
            }
          },
          sx: { cursor: "pointer" },
        })}
        renderRowActions={({ row }) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              color="primary"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenEditModal(row.original);
              }}
            >
              <EditIcon />
            </IconButton>
          </Box>
        )}
        muiToolbarAlertBannerProps={
          state.isError
            ? { color: "error", children: "Error loading plant data" }
            : undefined
        }
        state={{
          showProgressBars: !plants?.[0]?.id,
          showAlertBanner: state.isError,
        }}
      />
      {/* Create/Edit Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {modalMode === "create" ? "Create New Plant" : "Edit Plant"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <Autocomplete
              options={allClients}
              getOptionLabel={(option) =>
                option?.name
                  ? `${option.name}${option.prefix ? ` (${option.prefix})` : ""}`
                  : ""
              }
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              value={
                allClients.find((c) => c.id === currentPlant.clientId) || null
              }
              onChange={(_event, newValue) => {
                handleClientChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client"
                  required={modalMode === "create"}
                  helperText="Prefix is taken from the selected client"
                />
              )}
            />
            {modalMode === "create" ? (
              <Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                  }}
                >
                  <TextField
                    label="Prefix"
                    value={currentPlant.namePrefix}
                    disabled
                    required
                    sx={{ flex: "1 1 100px", minWidth: 100 }}
                    placeholder="Select client"
                    helperText={!currentPlant.clientId ? "From client" : " "}
                  />
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    -
                  </Box>
                  <TextField
                    label="Number"
                    value={currentPlant.nameNumber}
                    onChange={(e) => handleNameNumberChange(e.target.value)}
                    required
                    sx={{ flex: "1 1 140px", minWidth: 140 }}
                    placeholder="p001"
                    helperText="e.g. p001 or p-001"
                    inputProps={{ maxLength: 20 }}
                  />
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    color: "text.secondary",
                    fontSize: "0.875rem",
                  }}
                >
                  Name preview:{" "}
                  {buildPlantName(
                    currentPlant.namePrefix,
                    currentPlant.nameNumber,
                  ) || "—"}
                </Box>
              </Box>
            ) : (
              <TextField
                label="Plant Name"
                value={currentPlant.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                fullWidth
                required
              />
            )}
            <TextField
              label="Description"
              value={currentPlant.description || ""}
              onChange={(e) =>
                handleInputChange("description", e.target.value)
              }
              fullWidth
              multiline
              minRows={2}
              helperText="Optional plant description"
            />
            <TextField
              label="SharePoint Link"
              value={currentPlant.sharepointLink}
              onChange={(e) =>
                handleInputChange("sharepointLink", e.target.value)
              }
              fullWidth
              type="url"
              helperText="Optional SharePoint URL"
            />
            <Autocomplete
              multiple
              options={allUsers}
              getOptionLabel={(option) =>
                typeof option === "string"
                  ? allUsers.find((u) => u.id === option)?.name ||
                    allUsers.find((u) => u.id === option)?.email ||
                    option
                  : option.name || option.email || ""
              }
              isOptionEqualToValue={(option, value) => {
                const optionId =
                  typeof option === "string" ? option : option.id;
                const valueId = typeof value === "string" ? value : value.id;
                return optionId === valueId;
              }}
              value={
                currentPlant.people && allUsers.length > 0
                  ? currentPlant.people
                      .map((id) => allUsers.find((u) => u.id === id))
                      .filter((user) => user !== undefined)
                  : []
              }
              onChange={(event, newValue) => {
                handlePeopleChange(newValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="People"
                  helperText="Select users to associate with this plant"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  const label =
                    typeof option === "string"
                      ? allUsers.find((u) => u.id === option)?.name ||
                        allUsers.find((u) => u.id === option)?.email ||
                        option
                      : option.name || option.email || "";
                  return (
                    <Chip
                      variant="outlined"
                      label={label}
                      {...getTagProps({ index })}
                      key={typeof option === "string" ? option : option.id}
                    />
                  );
                })
              }
            />
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
